import {
  Badge,
  Box,
  Card,
  Flex,
  Grid,
  GridItem,
  HStack,
  Heading,
  Stack,
  Stat,
  Table,
  TabsContent,
  TabsList,
  TabsRoot,
  TabsTrigger,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { DashboardResourceLink } from "./DashboardResourceLink"

import { ProgramsService } from "@/client"
import type { UserPublic } from "@/client"
import type { MembershipPublic } from "@/client/types.gen"
import { HtmlContent } from "../Common/HtmlContent"

import { DashboardProgramFilter } from "@/components/Dashboard/DashboardProgramFilter"
import FlashcardsTab from "@/components/Dashboard/FlashcardsTab"
import { QuizActivityCharts } from "@/components/Dashboard/QuizActivityCharts"
import { QuizAnalyticsTable } from "@/components/Dashboard/QuizAnalyticsTable"
import { PageSkeleton } from "@/components/Layout"
import SchoolAnalytics from "@/components/Organizations/SchoolAnalytics"
import { determineOrgId } from "@/hooks/useBranding"
import { useTeacherDashboard } from "@/hooks/useDashboardQueries"
import { useFlashcardsEnabled } from "@/hooks/useFlashcardsEnabled"

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8"]
const MAX_PIE_SLICES = 10

type PieDatum = { name: string; count: number }

const formatPieData = (items: PieDatum[]) => {
  if (!items || items.length === 0) return []

  const sorted = [...items].sort((a, b) => b.count - a.count)

  if (sorted.length <= MAX_PIE_SLICES) {
    return sorted
  }

  const maxIndividual = MAX_PIE_SLICES - 1
  const visible = sorted.slice(0, maxIndividual)
  const otherTotal = sorted
    .slice(maxIndividual)
    .reduce((sum, item) => sum + item.count, 0)

  return [...visible, { name: "Other", count: otherTotal }]
}

export default function TeacherDashboard() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState("quizzes")
  const [selectedProgramIds, setSelectedProgramIds] = useState<string[]>([])
  const currentUser = queryClient.getQueryData<
    (UserPublic & { selected_org_id?: string }) | null
  >(["currentUser"])
  const currentOrgId = determineOrgId(queryClient)
  const normalizedProgramIds = useMemo(
    () => [...selectedProgramIds].sort(),
    [selectedProgramIds],
  )
  const dashboardProgramIds = normalizedProgramIds.length
    ? normalizedProgramIds
    : undefined
  const { data, isLoading } = useTeacherDashboard({
    programIds: dashboardProgramIds,
  })
  const { enabled: flashcardsEnabled } = useFlashcardsEnabled()
  const { data: programs, isLoading: programsLoading } = useQuery({
    queryKey: ["teacherDashboardPrograms", currentOrgId],
    queryFn: () => ProgramsService.listPrograms({ limit: 200 }),
    enabled: Boolean(currentOrgId),
  })
  const memberships =
    currentUser && !currentUser.is_superuser
      ? queryClient.getQueryData<MembershipPublic[]>([
          "myMemberships",
          currentUser.id,
        ]) ?? []
      : []
  const canViewSchoolAnalytics =
    Boolean(currentOrgId) &&
    (currentUser?.is_superuser === true ||
      memberships.some(
        (membership) =>
          membership.org_id === currentOrgId && membership.role === "admin",
      ))
  const questionLink = (questionId: string) => ({
    to: "/questions/$questionId/analytics" as const,
    params: { questionId },
  })
  const quizLink = (quizId: string) => ({
    to: "/quiz-management/$quizId/analytics" as const,
    params: { quizId },
  })
  const feedbackLink = (questionId: string, feedbackId?: string) => ({
    to: "/questions/$questionId/feedback" as const,
    params: { questionId },
    search: feedbackId ? { feedbackId } : undefined,
  })
  const subjectChartData = useMemo(
    () => formatPieData(data?.questions_by_subject ?? []),
    [data?.questions_by_subject],
  )
  const topicChartData = useMemo(
    () => formatPieData(data?.questions_by_topic ?? []),
    [data?.questions_by_topic],
  )

  if (isLoading) {
    return <PageSkeleton variant="stats" />
  }

  if (!data) {
    return <Text>No dashboard data available</Text>
  }

  return (
    <TabsRoot
      value={activeTab}
      onValueChange={({ value }) => setActiveTab(value)}
      variant="enclosed"
      mt={1}
      data-testid="teacher-dashboard"
    >
      <Flex justify="flex-end">
        <DashboardProgramFilter
          programs={programs?.data ?? []}
          selectedProgramIds={selectedProgramIds}
          onChange={(ids) => setSelectedProgramIds([...ids].sort())}
          isLoading={programsLoading}
        />
      </Flex>
      <TabsList overflowX="auto" overflowY="hidden" maxW="100%">
        <TabsTrigger value="quizzes" flexShrink={0}>
          Quizzes
        </TabsTrigger>
        {data.feedback_enabled && (
          <TabsTrigger value="feedback" flexShrink={0}>
            Feedback
            {data.unread_feedback_count > 0 && (
              <Badge colorPalette="red" ml={2}>
                {data.unread_feedback_count}
              </Badge>
            )}
          </TabsTrigger>
        )}
        {flashcardsEnabled && (
          <TabsTrigger
            value="flashcards"
            data-testid="flashcards-tab-trigger"
            flexShrink={0}
          >
            Flashcards
          </TabsTrigger>
        )}
        {canViewSchoolAnalytics && (
          <TabsTrigger value="school-analytics" flexShrink={0}>
            School Analytics
          </TabsTrigger>
        )}
      </TabsList>

      {/* Quizzes Tab */}
      <TabsContent value="quizzes">
        <Stack gap={6} data-testid="teacher-dashboard-quizzes">
          {/* Summary Cards */}
          <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4}>
            <GridItem>
              <Card.Root>
                <Card.Body>
                  <Stat.Root>
                    <Stat.Label>Active Quizzes</Stat.Label>
                    <Stat.ValueText>{data.total_active_quizzes}</Stat.ValueText>
                  </Stat.Root>
                </Card.Body>
              </Card.Root>
            </GridItem>

            <GridItem>
              <Card.Root>
                <Card.Body>
                  <Stat.Root>
                    <Stat.Label>Total Questions</Stat.Label>
                    <Stat.ValueText>{data.total_questions}</Stat.ValueText>
                  </Stat.Root>
                </Card.Body>
              </Card.Root>
            </GridItem>

            <GridItem>
              <Card.Root>
                <Card.Body>
                  <Stat.Root>
                    <Stat.Label>Attempt-weighted Avg Score</Stat.Label>
                    <Stat.ValueText>
                      {data.org_avg_score_percentage !== null
                        ? `${data.org_avg_score_percentage.toFixed(1)}%`
                        : "N/A"}
                    </Stat.ValueText>
                  </Stat.Root>
                </Card.Body>
              </Card.Root>
            </GridItem>

            <GridItem>
              <Card.Root>
                <Card.Body>
                  <Stat.Root>
                    <Stat.Label>Unused Questions</Stat.Label>
                    <Stat.ValueText>
                      {data.unused_questions_count}
                    </Stat.ValueText>
                  </Stat.Root>
                </Card.Body>
              </Card.Root>
            </GridItem>

            <GridItem>
              <Card.Root>
                <Card.Body>
                  <Stat.Root>
                    <Stat.Label>Started Attempts</Stat.Label>
                    <Stat.ValueText>
                      {data.total_started_attempts}
                    </Stat.ValueText>
                  </Stat.Root>
                </Card.Body>
              </Card.Root>
            </GridItem>

            <GridItem>
              <Card.Root>
                <Card.Body>
                  <Stat.Root>
                    <Stat.Label>Completed Attempts</Stat.Label>
                    <Stat.ValueText>
                      {data.total_completed_attempts}
                    </Stat.ValueText>
                  </Stat.Root>
                </Card.Body>
              </Card.Root>
            </GridItem>

            <GridItem>
              <Card.Root>
                <Card.Body>
                  <Stat.Root>
                    <Stat.Label>Attempt Completion Rate</Stat.Label>
                    <Stat.ValueText>
                      {data.attempt_completion_rate.toFixed(1)}%
                    </Stat.ValueText>
                  </Stat.Root>
                </Card.Body>
              </Card.Root>
            </GridItem>
          </Grid>

          <QuizActivityCharts rows={data.quiz_analytics} />

          <Card.Root data-testid="all-quiz-analytics">
            <Card.Header>
              <Heading size="md">All quizzes</Heading>
              <Text color="fg.muted" fontSize="sm">
                Includes quizzes with no attempts in the selected scope.
              </Text>
            </Card.Header>
            <Card.Body>
              <QuizAnalyticsTable rows={data.quiz_analytics} />
            </Card.Body>
          </Card.Root>

          {/* Quiz Status Pie Chart */}
          {data.quizzes_by_status.length > 0 && (
            <Card.Root data-testid="quiz-status-chart">
              <Card.Header>
                <Heading size="md">Quiz Status Breakdown</Heading>
              </Card.Header>
              <Card.Body>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={data.quizzes_by_status}
                      dataKey="count"
                      nameKey="status"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label
                    >
                      {data.quizzes_by_status.map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card.Root>
          )}

          {/* Performance Trend */}
          {data.performance_trends.length > 0 && (
            <Card.Root data-testid="performance-trend-chart">
              <Card.Header>
                <Heading size="md">Organization Performance Trend</Heading>
              </Card.Header>
              <Card.Body>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={data.performance_trends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis yAxisId="left" domain={[0, 100]} />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="avg_score_percentage"
                      stroke="#8884d8"
                      name="Avg Score %"
                      yAxisId="left"
                    />
                    <Line
                      type="monotone"
                      dataKey="student_count"
                      stroke="#82ca9d"
                      name="Active Students"
                      yAxisId="right"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card.Root>
          )}

          {/* Subject Performance */}
          {data.subject_performance.length > 0 && (
            <Card.Root data-testid="subject-performance-chart">
              <Card.Header>
                <Heading size="md">Subject Performance (Org-wide)</Heading>
              </Card.Header>
              <Card.Body>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.subject_performance}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="subject_name"
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis domain={[0, 100]} />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="correctness_rate"
                      fill="#8884d8"
                      name="Correctness Rate %"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card.Root>
          )}

          {/* Most Difficult Questions */}
          {data.most_difficult_questions.length > 0 && (
            <Card.Root>
              <Card.Header>
                <Heading size="md">Most Difficult Questions</Heading>
              </Card.Header>
              <Card.Body>
                {/* Desktop */}
                <Box display={{ base: "none", md: "block" }}>
                  <Table.Root size="sm" data-testid="difficult-questions-table">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>Question</Table.ColumnHeader>
                        <Table.ColumnHeader>Success Rate</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {data.most_difficult_questions.map((q) => (
                        <Table.Row
                          key={q.question_id}
                          data-testid="difficult-question-row"
                        >
                          <Table.Cell maxW="400px">
                            <Flex align="center" gap={2}>
                              <Box flex="1" minW={0} truncate>
                                <HtmlContent
                                  content={q.question_text}
                                  lineClamp={1}
                                  css={{
                                    "& p": { margin: 0, display: "inline" },
                                  }}
                                />
                              </Box>
                              <DashboardResourceLink
                                label="Open question analytics"
                                {...questionLink(q.question_id)}
                              />
                            </Flex>
                          </Table.Cell>
                          <Table.Cell>
                            <Badge
                              colorPalette={
                                q.correct_rate < 40
                                  ? "red"
                                  : q.correct_rate < 70
                                    ? "orange"
                                    : "green"
                              }
                            >
                              {q.correct_rate.toFixed(1)}%
                            </Badge>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </Box>

                {/* Mobile cards */}
                <VStack
                  display={{ base: "flex", md: "none" }}
                  gap={3}
                  align="stretch"
                >
                  {data.most_difficult_questions.map((q) => (
                    <Card.Root key={q.question_id}>
                      <Card.Body>
                        <HtmlContent
                          content={q.question_text}
                          lineClamp={2}
                          css={{ "& p": { margin: 0 } }}
                        />
                        <Flex justify="space-between" align="center" mt={2}>
                          <Badge
                            colorPalette={
                              q.correct_rate < 40
                                ? "red"
                                : q.correct_rate < 70
                                  ? "orange"
                                  : "green"
                            }
                          >
                            {q.correct_rate.toFixed(1)}%
                          </Badge>
                          <DashboardResourceLink
                            label="Open question analytics"
                            {...questionLink(q.question_id)}
                          />
                        </Flex>
                      </Card.Body>
                    </Card.Root>
                  ))}
                </VStack>
              </Card.Body>
            </Card.Root>
          )}
        </Stack>
      </TabsContent>

      {/* Question and content detail sections */}
      <TabsContent value="quizzes">
        <Stack gap={6} data-testid="teacher-dashboard-content">
          <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6}>
            {/* Questions by Subject */}
            {subjectChartData.length > 0 && (
              <GridItem>
                <Card.Root data-testid="questions-by-subject-chart">
                  <Card.Header>
                    <Heading size="md">Questions by Subject</Heading>
                  </Card.Header>
                  <Card.Body>
                    <ResponsiveContainer width="100%" height={320}>
                      <PieChart>
                        <Pie
                          data={subjectChartData}
                          dataKey="count"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={110}
                          label
                        >
                          {subjectChartData.map((_, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Card.Body>
                </Card.Root>
              </GridItem>
            )}

            {/* Questions by Topic */}
            {topicChartData.length > 0 && (
              <GridItem>
                <Card.Root data-testid="questions-by-topic-chart">
                  <Card.Header>
                    <Heading size="md">Questions by Topic</Heading>
                  </Card.Header>
                  <Card.Body>
                    <ResponsiveContainer width="100%" height={320}>
                      <PieChart>
                        <Pie
                          data={topicChartData}
                          dataKey="count"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={110}
                          label
                        >
                          {topicChartData.map((_, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Card.Body>
                </Card.Root>
              </GridItem>
            )}
          </Grid>

          {/* Recently Created Content */}
          {data.recently_created_content.length > 0 && (
            <Card.Root data-testid="recent-content-list">
              <Card.Header>
                <Heading size="md">Recently Created</Heading>
              </Card.Header>
              <Card.Body>
                <VStack align="stretch" gap={2}>
                  {data.recently_created_content.map((content) => (
                    <Box
                      key={content.id}
                      p={3}
                      borderWidth="1px"
                      borderRadius="md"
                    >
                      <Flex align="flex-start" gap={3}>
                        <Box flex="1" minW={0}>
                          <Badge
                            colorPalette={
                              content.type === "quiz" ? "blue" : "green"
                            }
                            mb={1}
                            textTransform="capitalize"
                          >
                            {content.type}
                          </Badge>
                          <Text fontWeight="medium">
                            <HtmlContent content={content.name} />
                          </Text>
                          <Text fontSize="sm" color="fg.muted">
                            Created by {content.created_by_name} on{" "}
                            {new Date(content.created_at).toLocaleDateString()}
                          </Text>
                        </Box>
                        <DashboardResourceLink
                          label={`Open ${content.type} analytics`}
                          {...(content.type === "quiz"
                            ? quizLink(content.id)
                            : questionLink(content.id))}
                        />
                      </Flex>
                    </Box>
                  ))}
                </VStack>
              </Card.Body>
            </Card.Root>
          )}
        </Stack>
      </TabsContent>

      {/* Feedback Tab */}
      {data.feedback_enabled && (
        <TabsContent value="feedback">
          <Stack gap={6} data-testid="teacher-dashboard-feedback">
            {/* Feedback Summary Cards */}
            <Grid
              templateColumns="repeat(auto-fit, minmax(200px, 1fr))"
              gap={4}
            >
              <GridItem>
                <Card.Root>
                  <Card.Body>
                    <Stat.Root>
                      <Stat.Label>Unread Feedback</Stat.Label>
                      <Stat.ValueText>
                        {data.unread_feedback_count}
                      </Stat.ValueText>
                    </Stat.Root>
                  </Card.Body>
                </Card.Root>
              </GridItem>

              <GridItem>
                <Card.Root>
                  <Card.Body>
                    <Stat.Root>
                      <Stat.Label>Assigned to Me</Stat.Label>
                      <Stat.ValueText>
                        {data.assigned_to_me_count}
                      </Stat.ValueText>
                    </Stat.Root>
                  </Card.Body>
                </Card.Root>
              </GridItem>

              <GridItem>
                <Card.Root>
                  <Card.Body>
                    <Stat.Root>
                      <Stat.Label>Unassigned</Stat.Label>
                      <Stat.ValueText>
                        {data.unassigned_feedback_count}
                      </Stat.ValueText>
                    </Stat.Root>
                  </Card.Body>
                </Card.Root>
              </GridItem>
            </Grid>

            {/* Feedback Status Breakdown */}
            {data.feedback_by_status.length > 0 && (
              <Card.Root>
                <Card.Header>
                  <Heading size="md">Feedback Status Breakdown</Heading>
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={data.feedback_by_status}
                        dataKey="count"
                        nameKey="status"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label
                      >
                        {data.feedback_by_status.map((_, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card.Root>
            )}

            {/* Recent Feedback Submissions */}
            {data.recent_feedback.length > 0 && (
              <Card.Root>
                <Card.Header>
                  <Heading size="md">Recent Feedback Submissions</Heading>
                </Card.Header>
                <Card.Body>
                  {/* Desktop */}
                  <Box display={{ base: "none", md: "block" }}>
                    <Table.Root
                      size="sm"
                      data-testid="dashboard-feedback-table"
                    >
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>Student</Table.ColumnHeader>
                          <Table.ColumnHeader>Quiz</Table.ColumnHeader>
                          <Table.ColumnHeader>Feedback</Table.ColumnHeader>
                          <Table.ColumnHeader>Assigned To</Table.ColumnHeader>
                          <Table.ColumnHeader>Status</Table.ColumnHeader>
                          <Table.ColumnHeader>Date</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {data.recent_feedback.map((feedback) => (
                          <Table.Row
                            key={feedback.feedback_id}
                            data-testid="dashboard-feedback-row"
                          >
                            <Table.Cell>
                              {feedback.submitted_by_name}
                            </Table.Cell>
                            <Table.Cell maxW="250px">
                              <Flex align="center" gap={2}>
                                <Text flex="1" minW={0} truncate>
                                  {feedback.quiz_name}
                                </Text>
                                <DashboardResourceLink
                                  label="Open quiz analytics"
                                  {...quizLink(feedback.quiz_id)}
                                />
                              </Flex>
                            </Table.Cell>
                            <Table.Cell maxW="300px">
                              <Flex align="center" gap={2}>
                                <Box flex="1" minW={0} truncate>
                                  <HtmlContent
                                    content={feedback.feedback_text}
                                    lineClamp={1}
                                    css={{
                                      "& p": { margin: 0, display: "inline" },
                                    }}
                                  />
                                </Box>
                                <DashboardResourceLink
                                  label="Open question feedback"
                                  {...feedbackLink(
                                    feedback.question_id,
                                    feedback.feedback_id,
                                  )}
                                />
                              </Flex>
                            </Table.Cell>
                            <Table.Cell>
                              {feedback.assigned_to_name ?? "—"}
                            </Table.Cell>
                            <Table.Cell>
                              <Badge
                                colorPalette={
                                  feedback.status === "unread"
                                    ? "red"
                                    : feedback.status === "read"
                                      ? "yellow"
                                      : "green"
                                }
                              >
                                {feedback.status}
                              </Badge>
                            </Table.Cell>
                            <Table.Cell>
                              {new Date(
                                feedback.created_at,
                              ).toLocaleDateString()}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </Box>

                  {/* Mobile cards */}
                  <VStack
                    display={{ base: "flex", md: "none" }}
                    gap={3}
                    align="stretch"
                  >
                    {data.recent_feedback.map((feedback) => (
                      <Card.Root key={feedback.feedback_id}>
                        <Card.Body>
                          <Stack gap={2}>
                            <Flex justify="space-between">
                              <Text fontSize="xs" color="fg.muted">
                                Student
                              </Text>
                              <Text fontSize="sm">
                                {feedback.submitted_by_name}
                              </Text>
                            </Flex>
                            <Flex
                              justify="space-between"
                              align="center"
                              gap={2}
                            >
                              <Text
                                fontSize="xs"
                                color="fg.muted"
                                flexShrink={0}
                              >
                                Quiz
                              </Text>
                              <Text
                                fontSize="sm"
                                flex="1"
                                minW={0}
                                truncate
                                textAlign="right"
                              >
                                {feedback.quiz_name}
                              </Text>
                            </Flex>
                            <Box>
                              <Text fontSize="xs" color="fg.muted" mb={1}>
                                Feedback
                              </Text>
                              <HtmlContent
                                content={feedback.feedback_text}
                                lineClamp={2}
                                css={{ "& p": { margin: 0 } }}
                              />
                            </Box>
                            <Flex justify="space-between">
                              <Text fontSize="xs" color="fg.muted">
                                Assigned To
                              </Text>
                              <Text fontSize="sm">
                                {feedback.assigned_to_name ?? "—"}
                              </Text>
                            </Flex>
                            <Flex justify="space-between">
                              <Text fontSize="xs" color="fg.muted">
                                Date
                              </Text>
                              <Text fontSize="sm">
                                {new Date(
                                  feedback.created_at,
                                ).toLocaleDateString()}
                              </Text>
                            </Flex>
                            <Flex justify="space-between" align="center" mt={1}>
                              <Badge
                                colorPalette={
                                  feedback.status === "unread"
                                    ? "red"
                                    : feedback.status === "read"
                                      ? "yellow"
                                      : "green"
                                }
                              >
                                {feedback.status}
                              </Badge>
                              <HStack gap={1}>
                                <DashboardResourceLink
                                  label="Open quiz analytics"
                                  {...quizLink(feedback.quiz_id)}
                                />
                                <DashboardResourceLink
                                  label="Open question feedback"
                                  {...feedbackLink(
                                    feedback.question_id,
                                    feedback.feedback_id,
                                  )}
                                />
                              </HStack>
                            </Flex>
                          </Stack>
                        </Card.Body>
                      </Card.Root>
                    ))}
                  </VStack>
                </Card.Body>
              </Card.Root>
            )}

            {/* Most Reported Questions */}
            {data.most_reported_questions.length > 0 && (
              <Card.Root>
                <Card.Header>
                  <Heading size="md">Most Reported Questions</Heading>
                </Card.Header>
                <Card.Body>
                  {/* Desktop */}
                  <Box display={{ base: "none", md: "block" }}>
                    <Table.Root
                      size="sm"
                      data-testid="reported-questions-table"
                    >
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>Question</Table.ColumnHeader>
                          <Table.ColumnHeader>Quiz</Table.ColumnHeader>
                          <Table.ColumnHeader>Total Reports</Table.ColumnHeader>
                          <Table.ColumnHeader>Unresolved</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {data.most_reported_questions.map((q) => (
                          <Table.Row
                            key={q.question_id}
                            data-testid="reported-question-row"
                          >
                            <Table.Cell maxW="400px">
                              <Flex align="center" gap={2}>
                                <Box flex="1" minW={0} truncate>
                                  <HtmlContent
                                    content={q.question_text}
                                    lineClamp={1}
                                    css={{
                                      "& p": { margin: 0, display: "inline" },
                                    }}
                                  />
                                </Box>
                                <DashboardResourceLink
                                  label="Open question analytics"
                                  {...questionLink(q.question_id)}
                                />
                              </Flex>
                            </Table.Cell>
                            <Table.Cell maxW="250px">
                              <Flex align="center" gap={2}>
                                <Text flex="1" minW={0} truncate>
                                  {q.quiz_name}
                                </Text>
                                <DashboardResourceLink
                                  label="Open quiz analytics"
                                  {...quizLink(q.quiz_id)}
                                />
                              </Flex>
                            </Table.Cell>
                            <Table.Cell>
                              <Badge colorPalette="blue">
                                {q.total_feedback_count}
                              </Badge>
                            </Table.Cell>
                            <Table.Cell>
                              <Badge colorPalette="red">
                                {q.unresolved_feedback_count}
                              </Badge>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </Box>

                  {/* Mobile cards */}
                  <VStack
                    display={{ base: "flex", md: "none" }}
                    gap={3}
                    align="stretch"
                  >
                    {data.most_reported_questions.map((q) => (
                      <Card.Root key={q.question_id}>
                        <Card.Body>
                          <HtmlContent
                            content={q.question_text}
                            lineClamp={2}
                            css={{ "& p": { margin: 0 } }}
                          />
                          <Text fontSize="sm" color="fg.muted" mt={1} truncate>
                            {q.quiz_name}
                          </Text>
                          <Flex justify="space-between" align="center" mt={2}>
                            <HStack gap={2}>
                              <Badge colorPalette="blue">
                                {q.total_feedback_count} total
                              </Badge>
                              <Badge colorPalette="red">
                                {q.unresolved_feedback_count} unresolved
                              </Badge>
                            </HStack>
                            <HStack gap={1}>
                              <DashboardResourceLink
                                label="Open question analytics"
                                {...questionLink(q.question_id)}
                              />
                              <DashboardResourceLink
                                label="Open quiz analytics"
                                {...quizLink(q.quiz_id)}
                              />
                            </HStack>
                          </Flex>
                        </Card.Body>
                      </Card.Root>
                    ))}
                  </VStack>
                </Card.Body>
              </Card.Root>
            )}
          </Stack>
        </TabsContent>
      )}
      {flashcardsEnabled && (
        <TabsContent value="flashcards">
          <FlashcardsTab
            isActive={activeTab === "flashcards"}
            programIds={dashboardProgramIds}
          />
        </TabsContent>
      )}
      {canViewSchoolAnalytics && currentOrgId && (
        <TabsContent value="school-analytics">
          <SchoolAnalytics
            orgId={currentOrgId}
            embedded
            programIds={selectedProgramIds}
            programs={programs?.data ?? []}
            programsLoading={programsLoading}
            hideProgramFilter
          />
        </TabsContent>
      )}
    </TabsRoot>
  )
}
